import { useEstabsContext } from '../../contexts/EstabsContext';
import { zonedDate } from '../../utils/date-time';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OfertasHorarioService } from './Service';
import SelectAtendentes from '../../components/inputs/SelectAtendentes';
import { useParamsContext } from '../../contexts/ParamsContext';
import { OfferDetails } from './OfferDetails';
import { formatOfferCurrency, formatOfferDay, formatOfferRange, getOfferStatus } from './helpers';
import './OfertasHorario.css';
import { ChevronRight, Megaphone, TriangleAlert } from 'lucide-react';

const ofertasService = new OfertasHorarioService();
const AUTO_REFRESH_INTERVAL_MS = 60 * 1000;

const countRecipients = (offer: any, field: 'sendStatus' | 'responseStatus', value: string) =>
    (Array.isArray(offer?.recipients) ? offer.recipients : []).filter((r: any) => r?.[field] === value).length;

export default function OfertasHorario() {
    const { estabSession } = useEstabsContext();
    const navigate = useNavigate();
    const { action, id, setParams } = useParamsContext();
    const todayKey = zonedDate(new Date(), estabSession?.timezone).format('YYYY-MM-DD');
    const [dateKey, setDateKey] = useState('');
    const [selectedAttendantId, setSelectedAttendantId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState<any>(null);
    const [quota, setQuota] = useState<any>(null);
    const requestIdRef = useRef(0);

    const openSchedule = (horarioId: string) => {
        navigate(`/agenda?action=view&id=${encodeURIComponent(horarioId)}`);
    };

    const load = useCallback(async () => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        try {
            setError(false);
            const res = await ofertasService.findAll(
                dateKey || undefined,
                selectedAttendantId || undefined,
            );

            if (requestIdRef.current !== requestId) {
                return;
            }

            setData(res.data);
        } catch (e) {
            // Falha na atualizacao automatica nao apaga a lista que ja esta na tela.
            if (requestIdRef.current === requestId) {
                setError(true);
            }
        } finally {
            if (requestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [dateKey, selectedAttendantId]);

    const loadQuota = useCallback(async () => {
        try {
            const res = await ofertasService.quota(todayKey);
            setQuota(res.data);
        } catch (e) {
            setQuota(null);
        }
    }, [todayKey]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    useEffect(() => {
        loadQuota();
    }, [loadQuota]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            if (document.hidden) return;
            load();
            loadQuota();
        }, AUTO_REFRESH_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [load, loadQuota]);

    const offers: any[] = useMemo(() => (Array.isArray(data?.offers) ? data.offers : []), [data?.offers]);

    const totals = useMemo(() => {
        const result = { revenue: 0, bookings: 0, minutes: 0, sent: 0, booked: 0 };

        offers.forEach((offer) => {
            result.revenue += Number(offer?.metrics?.recoveredRevenue || 0);
            result.bookings += Number(offer?.metrics?.bookings || 0);
            result.minutes += Number(offer?.metrics?.filledMinutes || 0);
            result.sent += countRecipients(offer, 'sendStatus', 'ENVIADO');
            result.booked += countRecipients(offer, 'responseStatus', 'AGENDOU');
        });

        return result;
    }, [offers]);

    const days = useMemo(() => {
        const groups: { key: string; label: string; offers: any[] }[] = [];

        offers.forEach((offer) => {
            const key = zonedDate(offer?.date, offer?.timezone).format('YYYY-MM-DD');
            const current = groups[groups.length - 1];

            if (current?.key === key) {
                current.offers.push(offer);
            } else {
                groups.push({ key, label: formatOfferDay(offer?.date, offer?.timezone), offers: [offer] });
            }
        });

        return groups;
    }, [offers]);

    if (action === 'detalhe' && id) {
        return (
            <div className="ofe-page px-2">
                <OfferDetails offerId={id} onOpenSchedule={openSchedule} />
            </div>
        );
    }

    const hasFilters = !!dateKey || !!selectedAttendantId;
    const conversion = totals.sent > 0 ? Math.round((totals.booked / totals.sent) * 100) : 0;

    return (
        <div className="ofe-page ofe-list px-2">
            <div className="ofe-toolbar">
                <div className="sf-segmented ofe-period" role="group" aria-label="Período">
                    <button type="button" aria-pressed={!dateKey} onClick={() => setDateKey('')}>
                        Todas
                    </button>
                    <button type="button" aria-pressed={dateKey === todayKey} onClick={() => setDateKey(todayKey)}>
                        Hoje
                    </button>
                </div>
                <input
                    className="sf-select-field ofe-date"
                    type="date"
                    value={dateKey}
                    onChange={(e) => setDateKey(e.target.value)}
                    aria-label="Filtrar por dia"
                />
                <div className="ofe-attendant">
                    <SelectAtendentes
                        name="ofertas-attendant-filter"
                        value={selectedAttendantId}
                        setValue={setSelectedAttendantId}
                        multiple={false}
                        placeholder="Todos os atendentes"
                        menuOverlay
                    />
                </div>
                {!!quota && (
                    <div className="ofe-quota" title="Limite diário de envios do estabelecimento">
                        <strong>{quota.used} de {quota.limit}</strong> envios hoje
                    </div>
                )}
            </div>

            {loading && !data ? (
                <div className="space-y-2" aria-busy="true">
                    <div className="sf-skeleton h-20" />
                    {[0, 1, 2].map((item) => (
                        <div key={item} className="sf-skeleton h-16" />
                    ))}
                </div>
            ) : error && !data ? (
                <div className="sf-state">
                    <TriangleAlert aria-hidden />
                    <h2>Não foi possível carregar as ofertas</h2>
                    <button type="button" className="btn btn-primary btn-sm" onClick={load}>
                        Tentar novamente
                    </button>
                </div>
            ) : offers.length === 0 ? (
                <div className="sf-state">
                    <Megaphone aria-hidden />
                    <h2>{hasFilters ? 'Nenhuma oferta encontrada' : 'Nenhuma oferta enviada'}</h2>
                    {hasFilters ? (
                        <button
                            type="button"
                            className="sf-link"
                            onClick={() => {
                                setDateKey('');
                                setSelectedAttendantId('');
                            }}
                        >
                            Limpar filtros
                        </button>
                    ) : (
                        <>
                            <p>Para ocupar um horário vago, selecione a faixa livre na agenda e toque em Oferta.</p>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/agenda')}>
                                Abrir agenda
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <div className="sf-group ofe-stats">
                        <div>
                            <strong>{formatOfferCurrency(totals.revenue)}</strong>
                            <span>Receita recuperada</span>
                        </div>
                        <div>
                            <strong>{totals.bookings}</strong>
                            <span>Agendamentos</span>
                        </div>
                        <div>
                            <strong>{totals.minutes} min</strong>
                            <span>Preenchidos</span>
                        </div>
                        <div>
                            <strong>{conversion}%</strong>
                            <span>
                                Conversão de {totals.sent} {totals.sent === 1 ? 'envio' : 'envios'}
                            </span>
                        </div>
                    </div>

                    {days.map((day) => (
                        <section key={day.key} className="mt-5">
                            <h2 className="sf-group-title">{day.label}</h2>
                            <ul className="sf-group">
                                {day.offers.map((offer) => {
                                    const status = getOfferStatus(offer);
                                    const discount = Number(offer?.discountPercent || 0);
                                    const sent = countRecipients(offer, 'sendStatus', 'ENVIADO');
                                    const booked = countRecipients(offer, 'responseStatus', 'AGENDOU');
                                    const failed = countRecipients(offer, 'sendStatus', 'FALHA_ENVIO');

                                    return (
                                        <li key={offer?._id}>
                                            <button
                                                type="button"
                                                className="ofe-offer"
                                                onClick={() => setParams({ action: 'detalhe', id: offer?._id })}
                                            >
                                                <span className="ofe-offer-main">
                                                    <span className="ofe-title">{formatOfferRange(offer)}</span>
                                                    <span className="sf-tags">
                                                        <span className="sf-tag" data-tone={status.tone}>
                                                            {status.label}
                                                        </span>
                                                        {discount > 0 && (
                                                            <span className="sf-tag" data-tone="green">
                                                                {discount}% de desconto
                                                            </span>
                                                        )}
                                                    </span>
                                                </span>
                                                <span className="ofe-sub ofe-offer-cell">
                                                    {offer?.attendant?.displayName || 'Atendente não identificado'}
                                                </span>
                                                <span className="ofe-sub ofe-offer-cell">
                                                    {sent} {sent === 1 ? 'enviada' : 'enviadas'} · {booked}{' '}
                                                    {booked === 1 ? 'agendou' : 'agendaram'}
                                                    {failed > 0 && <span className="text-error"> · {failed} com falha</span>}
                                                </span>
                                                <span className="ofe-money">
                                                    {formatOfferCurrency(offer?.metrics?.recoveredRevenue)}
                                                </span>
                                                <ChevronRight className="ofe-chevron" aria-hidden />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    ))}
                </>
            )}
        </div>
    );
}
